import React from 'react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CATEGORY_COLORS, formatPrice, VEHICLE_SPECS } from '../data/components';
import { Trash2, Package, Weight, IndianRupee } from 'lucide-react';

export default function SummaryPanel({ components, placedComponents, totalCost, totalWeight, onRemove, readOnly = false }) {
  const placedWithDetails = placedComponents.map(pc => {
    const comp = components.find(c => c.comp_id === pc.comp_id);
    return { ...pc, comp };
  }).filter(pc => pc.comp);

  const weightPercent = Math.min((totalWeight / VEHICLE_SPECS.maxWeight) * 100, 100);

  return (
    <div className="flex flex-col h-full" data-testid="summary-panel-content">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <h2 className="font-chivo font-semibold text-sm text-white mb-1">Design Summary</h2>
        <p className="text-[10px] text-slate-500 font-jetbrains uppercase tracking-[0.15em]">
          {placedComponents.length} component{placedComponents.length !== 1 ? 's' : ''} placed
        </p>
      </div>

      {/* Stats */}
      <div className="px-4 py-3 border-b border-white/5 grid grid-cols-2 gap-3">
        <div className="glass-card rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <IndianRupee className="w-3 h-3 text-blue-400" />
            <span className="text-[10px] text-slate-500 font-jetbrains uppercase tracking-wider">Total Cost</span>
          </div>
          <span className="font-chivo font-bold text-lg text-white" data-testid="total-cost">
            {formatPrice(totalCost)}
          </span>
        </div>
        <div className="glass-card rounded-lg p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Weight className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] text-slate-500 font-jetbrains uppercase tracking-wider">Weight</span>
          </div>
          <span className="font-chivo font-bold text-lg text-white" data-testid="total-weight">
            {totalWeight}kg
          </span>
          <div className="mt-1.5 h-1 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${weightPercent > 80 ? 'bg-red-500' : weightPercent > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${weightPercent}%` }}
            />
          </div>
          <span className="text-[9px] text-slate-600 mt-0.5 block">{VEHICLE_SPECS.maxWeight}kg max</span>
        </div>
      </div>

      {/* Component List */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {placedWithDetails.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-600">
              <Package className="w-8 h-8 mb-3 opacity-50" />
              <p className="text-xs text-center">Drag components from the library<br />to start designing</p>
            </div>
          ) : (
            placedWithDetails.map(pc => {
              const colors = CATEGORY_COLORS[pc.comp.category] || CATEGORY_COLORS.Storage;
              return (
                <div
                  key={pc.instance_id}
                  data-testid={`summary-item-${pc.instance_id}`}
                  className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/30 border border-white/5 group hover:border-white/10 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-md ${colors.bg} flex items-center justify-center shrink-0`}>
                    <span className={`text-[10px] font-bold ${colors.text}`}>
                      {pc.comp.grid_w}x{pc.comp.grid_h}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-200 truncate">{pc.comp.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="secondary" className={`text-[9px] h-4 ${colors.bg} ${colors.text} border-none`}>
                        {pc.comp.category}
                      </Badge>
                      <span className="font-jetbrains text-[10px] text-slate-500">
                        ({pc.x},{pc.y})
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="font-jetbrains text-xs text-blue-400">{formatPrice(pc.comp.price)}</span>
                    {!readOnly && (
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`summary-remove-${pc.instance_id}`}
                        onClick={() => onRemove(pc.instance_id)}
                        className="h-5 w-5 p-0 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
